import {
  MonthSummaryCards,
  type MonthSummaryCard,
} from '@/components/month-summary-cards'
import { SVGIcon } from '@/components_deprecated'
import { format } from '@/utils/date-fns'
import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  formatSecondsAsHhmm,
  type SalaryMonthSummary,
} from './utils/aggregate-month-summary'

interface SalaryMonthSummaryCardsProps {
  month: Date
  rangeStart: Date
  rangeEnd: Date
  summary: SalaryMonthSummary
  loading?: boolean
  collapsed?: boolean
  onToggleCollapsed?: () => void
}

/**
 * Salary-view adapter: maps an aggregated `SalaryMonthSummary` to the
 * shared `MonthSummaryCards` primitive.
 */
export const SalaryMonthSummaryCards: React.FC<
  SalaryMonthSummaryCardsProps
> = ({
  month,
  rangeStart,
  rangeEnd,
  summary,
  loading,
  collapsed,
  onToggleCollapsed,
}) => {
  const translations = useTranslate({
    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    totalWork: 'common.total-work',
    pause: 'common.pause',
    rest: 'common.rest',
    paidRest: 'activities.paid-pause',
    showSums: 'common.show-sums',
    hideSums: 'common.hide-sums',
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'activities.sum-overtime',
    available: 'activities.availability-time',
    plusNNextMonth: ['activities.plus-n-next-month', { n: '' }],
    scopeInfo: ['salary.month-summary-scope-info', { start: '', end: '' }],
  })

  // const showNextMonth =
  //   summary.hasUseWeekOvertime && summary.overtimeNextMonthSeconds > 0

  const cards: MonthSummaryCard[] = [
    {
      key: 'totalWork',
      label: translations.totalWork,
      value: formatSecondsAsHhmm(
        summary.drivingSeconds +
          summary.otherWorkSeconds +
          summary.availabilitySeconds +
          summary.paidRestSeconds
      ),
      iconBg: '#344054',
      icon: (
        <Icon icon="business-time" fixedWidth size="2.36em" color="white" />
      ),
    },
    {
      key: 'driving',
      label: translations.driving,
      value: formatSecondsAsHhmm(summary.drivingSeconds),
      iconBg: '#399D26',
      icon: <SVGIcon icon="truck" />,
    },
    {
      key: 'otherWork',
      label: translations.otherWork,
      value: formatSecondsAsHhmm(summary.otherWorkSeconds),
      iconBg: '#ffb600',
      icon: <Icon icon="briefcase" fixedWidth size="2.92em" />,
    },
    {
      key: 'availability',
      label: translations.available,
      value: formatSecondsAsHhmm(summary.availabilitySeconds),
      iconBg: '#00c7b1',
      icon: (
        <Icon
          icon="hand-holding"
          fixedWidth
          translateY="-0.8rem"
          size="2.622em"
        />
      ),
    },
    // {
    //   key: 'rest',
    //   label: translations.rest,
    //   value: formatSecondsAsHhmm(summary.restSeconds),
    //   iconBg: '#8a8fff',
    //   icon: <Icon icon="bed" fixedWidth size="2.36em" />,
    // },
    {
      key: 'pause',
      label: translations.pause,
      value: formatSecondsAsHhmm(summary.pauseSeconds),
      iconBg: '#4C9CF1',
      icon: <Icon icon="pause" fixedWidth size="2.92em" />,
    },
    {
      key: 'paidRest',
      label: translations.paidRest,
      value: formatSecondsAsHhmm(summary.paidRestSeconds),
      iconBg: '#b39ddb',
      icon: <Icon icon="mug-hot" fixedWidth size="2.92em" />,
    },
    // {
    //   key: 'overtime',
    //   label: translations.overtime,
    //   value: formatSecondsAsHhmm(summary.overtimeSeconds),
    //   iconBg: '#fabbce',
    //   icon: <SVGIcon icon="clock" />,
    //   hint: showNextMonth
    //     ? translations.plusNNextMonth({
    //         n: formatSecondsAsHhmm(summary.overtimeNextMonthSeconds),
    //       })
    //     : undefined,
    // },
  ]

  return (
    <MonthSummaryCards
      title={translations.sumForMonth({ month: format(month, 'MMMM') })}
      cards={cards}
      loading={loading}
      titleTooltip={translations.scopeInfo({
        start: format(rangeStart, 'd. MMM yyyy'),
        end: format(rangeEnd, 'd. MMM yyyy'),
      })}
      collapsed={collapsed}
      onToggleCollapsed={onToggleCollapsed}
      toggleLabel={collapsed ? translations.showSums : translations.hideSums}
    />
  )
}
